import {
  abs,
  cameraPosition,
  clamp,
  Fn,
  float,
  floor,
  fract,
  fwidth,
  max,
  min,
  mix,
  positionLocal,
  positionWorld,
  pow,
  smoothstep,
  step,
  uniform,
  vec3,
  vec4,
} from "three/tsl";
import * as THREE from "three/webgpu";
import {
  COLOR_AXIS_X_HEX,
  COLOR_AXIS_Y_HEX,
  COLOR_AXIS_Z_HEX,
} from "../ui/tokens.ts";
import { GRID_EXTENT } from "../units.ts";

/**
 * Positive polygon offset pushes the grid slightly away from the camera so
 * real scene surfaces win when they are coplanar with the ground plane.
 */
export const GROUND_GRID_POLYGON_OFFSET_FACTOR = 1;
export const GROUND_GRID_POLYGON_OFFSET_UNITS = 1;

/**
 * Shared log-distance uniform driving every grid renderer's LOD step.
 * `setGroundGridLogDist` updates it for the world-floor grid AND every
 * other grid material that imports this node (e.g. the surface
 * alignment grid), so all grids zoom in lock-step.
 */
export const gridLogDistNode = uniform(1.0);

export function setGroundGridLogDist(logDist: number): void {
  gridLogDistNode.value = logDist;
}

/**
 * The plane a grid material renders on. The mesh itself is always authored
 * as a unit quad in its own local XY; the world-space orientation is chosen
 * by the caller (rotation of the mesh transform). The `planeAxes` parameter
 * tells the shader which two world axes the plane spans so the correct two
 * axis colors (red X / green Y / blue Z) are painted onto the axis lines.
 */
export type GridPlaneAxes = "xy" | "xz" | "yz";

/**
 * Build a grid material for the given world plane. Each instance is a
 * separate NodeMaterial (the TSL fragment graph bakes the axis colors), but
 * they share the `groundGridLogDistNode` uniform for LOD so
 * `setGroundGridLogDist` drives all of them at once.
 */
export function createGroundGridMaterial(
  planeAxes: GridPlaneAxes = "xy",
): THREE.NodeMaterial {
  const mat = new THREE.NodeMaterial();
  mat.transparent = true;
  mat.side = THREE.DoubleSide;
  mat.depthWrite = false;
  mat.polygonOffset = true;
  mat.polygonOffsetFactor = GROUND_GRID_POLYGON_OFFSET_FACTOR;
  mat.polygonOffsetUnits = GROUND_GRID_POLYGON_OFFSET_UNITS;
  mat.name = `GridMaterial_${planeAxes}`;

  // Axis line colors: the "horizontal" line of the grid (y=0 in local space)
  // is tinted as the world axis that runs along local X; vice versa for
  // "vertical". Concretely:
  //   xy plane: local X = world X → red; local Y = world Y → green
  //   xz plane: local X = world X → red; local Y = world Z → blue
  //   yz plane: local X = world Y → green; local Y = world Z → blue
  const axisHexForLocalX =
    planeAxes === "yz" ? COLOR_AXIS_Y_HEX : COLOR_AXIS_X_HEX;
  const axisHexForLocalY =
    planeAxes === "xy" ? COLOR_AXIS_Y_HEX : COLOR_AXIS_Z_HEX;
  const colorX = new THREE.Color(axisHexForLocalX);
  const colorY = new THREE.Color(axisHexForLocalY);

  mat.fragmentNode = Fn(() => {
    const pos = positionLocal.xy;
    const deriv = fwidth(pos);

    // logDist is log10(cameraDistance)
    // We cover 0.01m (cm) to 10m+ unit scales.
    // Clamping to [-1, 1] allows three discrete "views":
    // Fine: level -1 (0.01, 0.1, 1)
    // Standard: level 0 (0.1, 1, 10)
    // Coarse: level 1 (1, 10, 100)
    // We apply a 0.5 bias so granular levels activate "farther back".
    const clampedLogDist = clamp(gridLogDistNode.sub(0.5), -1.0, 1.0);
    const level = floor(clampedLogDist);
    const step1 = pow(10, level);
    const step10 = step1.mul(10);
    const step01 = step1.div(10);

    // f is the fractional transition between levels [0, 1]
    const f = fract(clampedLogDist);

    // Grid level helper
    const gridAt = (stepSize: any) => {
      const g = abs(fract(pos.div(stepSize).sub(0.5)).sub(0.5)).div(
        deriv.div(stepSize),
      );
      return float(1.5).sub(min(g.x, g.y)).clamp(0, 1);
    };

    const l01 = gridAt(step01); // 0.01 if level -1
    const l1 = gridAt(step1); // 0.1 if level -1
    const l10 = gridAt(step10); // 1.0 if level -1

    // Axis lines (X=red, Y=green) — wider footprint, fully opaque
    const isAxisX = float(1.0)
      .sub(smoothstep(float(0), deriv.y.mul(1.5), abs(pos.y)))
      .clamp(0, 1);
    const isAxisY = float(1.0)
      .sub(smoothstep(float(0), deriv.x.mul(1.5), abs(pos.x)))
      .clamp(0, 1);
    const onAxis = max(isAxisX, isAxisY);

    // Opacities — suppress grid lines where an axis line dominates to avoid
    // the white halo caused by major-line antialiasing bleeding past the axis.
    //
    // Transitions:
    // logDist = -2 (0.01m): opacity01 (major), opacity1 (major), opacity10 (minor)
    // logDist = -1 (0.1m): opacity01 (minor), opacity1 (major), opacity10 (major)
    // logDist = 0 (1.0m): opacity01 (fade out), opacity1 (minor), opacity10 (major)
    const opacity01 = l01
      .mul(0.0225)
      .mul(float(1.0).sub(f))
      .mul(float(1.0).sub(onAxis));
    const opacity1 = l1
      .mul(mix(float(0.06), float(0.0225), f))
      .mul(float(1.0).sub(onAxis));
    const opacity10 = l10.mul(0.06).mul(float(1.0).sub(onAxis));

    const opacityAxisX = isAxisX.mul(0.8);
    const opacityAxisY = isAxisY.mul(0.8);

    // Base color is white for the grid
    const gridColor = vec3(1, 1, 1);

    // Choose axis color if on an axis, otherwise white
    const axisColX = vec3(colorX.r, colorX.g, colorX.b);
    const axisColY = vec3(colorY.r, colorY.g, colorY.b);
    const axisColor = mix(axisColX, axisColY, isAxisY);

    const finalColor = mix(gridColor, axisColor, step(float(0.001), onAxis));

    // Edge fade — starts at 40% of the grid radius so lines dissolve gradually
    const dist = max(abs(pos.x), abs(pos.y));
    const fade = float(1.0).sub(
      smoothstep(float((GRID_EXTENT / 2) * 0.1), float(GRID_EXTENT / 2), dist),
    );

    // Grazing-angle fade — computed per-fragment so panning the origin off-center
    // gives correct per-pixel elevation angles rather than using the single
    // center-screen ray. |normalComp| / |camToFrag| is the sine of the elevation
    // angle above the grid plane at each fragment.
    const camToFrag = positionWorld.sub(cameraPosition);
    const camToFragLen = max(camToFrag.length(), float(0.001));
    const normalComp =
      planeAxes === "xy"
        ? camToFrag.z
        : planeAxes === "xz"
          ? camToFrag.y
          : camToFrag.x;
    const grazingFade = smoothstep(
      float(0.08),
      float(0.25),
      abs(normalComp).div(camToFragLen),
    );
    const grazingSeverity = float(1.0).sub(grazingFade);

    // Per-axis foreshortening — also per-fragment: the alignment of the view ray
    // at this point with each local axis direction, so axis lines that recede
    // toward the vanishing point fade independently from those crossing the view.
    //   local X axis:  xy→camToFrag.x,  xz→camToFrag.x,  yz→camToFrag.y
    //   local Y axis:  xy→camToFrag.y,  xz→camToFrag.z,  yz→camToFrag.z
    const foreshortX = (
      planeAxes === "yz" ? abs(camToFrag.y) : abs(camToFrag.x)
    ).div(camToFragLen);
    const foreshortY = (
      planeAxes === "xy" ? abs(camToFrag.y) : abs(camToFrag.z)
    ).div(camToFragLen);
    const axisXFade = float(1.0).sub(grazingSeverity.mul(foreshortX));
    const axisYFade = float(1.0).sub(grazingSeverity.mul(foreshortY));

    const gridOpacity = max(max(opacity01, opacity1), opacity10).mul(
      grazingFade,
    );
    const opacityAxisFaded = max(
      opacityAxisX.mul(axisXFade),
      opacityAxisY.mul(axisYFade),
    );
    const finalOpacity = max(gridOpacity, opacityAxisFaded).mul(fade);

    return vec4(finalColor, finalOpacity);
  })();

  return mat;
}

/**
 * Three pre-built grid materials, one per world plane. The XY variant is the
 * perspective/default floor grid; the XZ and YZ variants light up when the
 * viewport snaps to Front/Back or Left/Right axis-views respectively.
 */
export const groundGridMaterialXY = createGroundGridMaterial("xy");
export const groundGridMaterialXZ = createGroundGridMaterial("xz");
export const groundGridMaterialYZ = createGroundGridMaterial("yz");

/** Back-compat alias — the XY ground grid was previously the only variant. */
export const groundGridMaterial = groundGridMaterialXY;
